// Setup › Price book › Pictures › To approve (BL-122), in Blueprint only, for the owner and managers
// (pricebook.edit): the new pictures waiting before customers and technicians see them, in a grid, each
// light and dark beside what it shows and the items sharing it. "Approve" puts one live; "Turn down" asks
// for another (with what it should show, if they say), made fresh and counted against the month's
// budget; "Approve all shown" approves the grid. Each answer tells the Setup rail's count to look again.
import { useEffect, useState } from 'react';
import { client } from '../api.ts';
import { useDesign } from '../blueprint/design.ts';
import { refusal } from '../history/customer-edit-model.ts';
import {
  approvedWords,
  itemsWords,
  PICTURES_CHANGED,
  turnedDownWords,
  waitingLine,
  type Approved,
  type ToApprove,
} from './pictures-model.ts';

const SHOWN = 60;

export function PicturesToApprove({ canEdit }: { canEdit: boolean }) {
  const bp = useDesign() === 'blueprint';
  const [list, setList] = useState<{ pictures: ToApprove[]; total: number } | null>(null);
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const [request, setRequest] = useState('');
  useEffect(() => {
    if (!bp || !canEdit) return;
    let live = true;
    client
      .call<{ pictures: ToApprove[]; total: number }>('pricebook.art.toApprove', { limit: SHOWN })
      .then((r) => live && setList(r))
      .catch((e: unknown) => live && setSaid(refusal(e)));
    return () => {
      live = false;
    };
  }, [bp, canEdit, version]);
  if (!bp || !canEdit || !list) return null;

  const done = (words: string) => {
    setSaid(words);
    setAsking(null);
    setRequest('');
    setVersion((v) => v + 1);
    window.dispatchEvent(new Event(PICTURES_CHANGED));
  };
  const approve = async (artIds: string[]) => {
    setBusy(true);
    setSaid(null);
    try {
      done(approvedWords(await client.call<Approved>('pricebook.art.approve', { artIds })));
    } catch (e) {
      setSaid(refusal(e));
    }
    setBusy(false);
  };
  const turnDown = async (artId: string) => {
    setBusy(true);
    setSaid(null);
    try {
      const r = await client.call<{ rejected: boolean; jobId: string | null }>('pricebook.art.reject', {
        artId,
        ...(request.trim() ? { request: request.trim() } : {}),
      });
      done(turnedDownWords(r));
    } catch (e) {
      setSaid(refusal(e));
    }
    setBusy(false);
  };

  return (
    <section className="flw flw-office pb-art-approve" aria-labelledby="pb-art-approve-h">
      <h3 id="pb-art-approve-h">To approve</h3>
      <p className="flw-sub">{waitingLine(list.total, list.pictures.length)}</p>
      {list.pictures.length > 1 && (
        <div className="flw-acts">
          <button
            type="button"
            className="flw-btn flw-btn-pri"
            disabled={busy}
            onClick={() => void approve(list.pictures.map((p) => p.artId))}
          >
            Approve all {list.pictures.length} shown
          </button>
        </div>
      )}
      {said && (
        <p className="flw-sub" role="status">
          {said}
        </p>
      )}
      {list.pictures.length > 0 && (
        <ul className="flw-approve-grid">
          {list.pictures.map((p) => (
            <li key={p.artId} className="flw-approve-card">
              <div className="flw-approve-pics">
                {(['light', 'dark'] as const).map((t) => {
                  const src = t === 'light' ? p.light.url : p.dark?.url;
                  return src ? (
                    <img key={t} src={src} alt={`${p.words}, the ${t} picture`} />
                  ) : (
                    <p key={t} className="flw-empty">
                      No {t} picture
                    </p>
                  );
                })}
              </div>
              <p className="flw-approve-name">{itemsWords(p)}</p>
              <p className="flw-sub">
                {p.look === 'label' ? 'Stands for: ' : 'Shows: '}
                {p.words}
                {p.fromLibrary ? '. From Flowology’s picture library.' : '.'}
                {p.request ? ` Asked for: “${p.request}”.` : ''}
              </p>
              {asking === p.artId ? (
                <div className="flw-art-ask">
                  <label className="flw-fld">
                    <span>What should the new one show?</span>
                    <textarea
                      className="flw-field flw-art-text"
                      value={request}
                      maxLength={300}
                      rows={3}
                      placeholder="Leave it empty for the house style"
                      onChange={(e) => setRequest(e.target.value)}
                    />
                  </label>
                  <p className="flw-sub">
                    Another is made in its place, and counts against this month’s picture budget.
                  </p>
                  <div className="flw-acts">
                    <button
                      type="button"
                      className="flw-btn flw-btn-pri"
                      disabled={busy}
                      onClick={() => void turnDown(p.artId)}
                    >
                      {busy ? 'Asking…' : 'Turn down and make another'}
                    </button>
                    <button type="button" className="flw-btn" onClick={() => setAsking(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flw-acts">
                  <button
                    type="button"
                    className="flw-btn flw-btn-pri"
                    disabled={busy}
                    onClick={() => void approve([p.artId])}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="flw-btn"
                    disabled={busy}
                    onClick={() => {
                      setAsking(p.artId);
                      setRequest('');
                    }}
                  >
                    Turn down
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
